/**
 * GameActions - ゲームアクションの定義と適用
 *
 * 全てのゲームアクションを純粋関数として実装。
 * applyAction(state, action) → newState の形式で状態を更新。
 */

import { clearTemporaryAttack } from '../temporaryAttack';
import {
  getPlayer,
  getOpponent,
  getCurrentPlayer,
  getCurrentOpponent,
  getOpponentNumber,
  updatePlayer,
  updateTurnFlags,
  updateGameProgress,
  addLog,
  checkWinner,
  canSummonCard,
  getSummonCost,
  canUseMagic,
  canMonsterAttack,
  hasMonsterOnField,
  PHASES,
  PHASE_NAMES,
  GAME_STATUS,
  type GameState,
  type PlayerState,
  type PhaseType,
  type LogEntry,
  type PlayedCardEntry,
} from './GameState';

import { MAX_SP, COUNTER_ATTACK_RATE, PIERCING_DAMAGE_RATE, LONE_WARRIOR_BONUS } from '../../utils/constants';
import { statusEffectEngine, getStatusDisplayName } from '../statusEffects';
import type { PlayerStatusEffect } from '../statusEffects/statusEngine';
import { createMonsterInstance } from '../../utils/helpers';
import { TRIGGER_TYPES } from '../triggerTypes';
import {
  getValidAttackTargets,
  getDirectAttackStatus,
  getCounterAttackMultiplier,
  calculateFlightCounterDamage,
  hasHikou,
  isGroundMonster,
  hasFlightEffect,
} from '../keywordAbilities';
import type { CardWithId, MonsterInstance, CardData, CardType, PhaseCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import { registerCardTriggersPure, fireTriggerPure, parseCardTriggers } from './triggerEnginePure';
import {
  unlockDeepSeaZone,
  submergeCard,
  surfaceCard,
  incrementDepth,
  setDeepSeaCapacity,
  destroyDeepSeaCard,
  getDepthBonus,
  hasAnkokuSuiikiKeyword,
} from '../deepSeaZone';
import { getSummonBuffsPure } from './continuousEffectEnginePure';
import { ACTIVATION_TYPES } from '../triggerTypes';

/** 一時的なコスト軽減を持つカード */
interface CardWithTempModifier extends CardWithId {
  tempCostModifier?: number;
  tempCostModifierSource?: string;
  tempCostModifierOneTime?: boolean;
}

// ========================================
// アクションタイプ定義
// ========================================

export const ACTION_TYPES = {
  // フェイズ進行
  PROCESS_PHASE: 'PROCESS_PHASE',
  NEXT_PHASE: 'NEXT_PHASE',
  END_TURN: 'END_TURN',
  SET_PHASE: 'SET_PHASE', // Phase B: 直接フェイズ設定用

  // カードアクション
  SUMMON_CARD: 'SUMMON_CARD',
  ATTACK: 'ATTACK',
  EXECUTE_SKILL: 'EXECUTE_SKILL',
  CHARGE_CARD: 'CHARGE_CARD',
  CHARGE_SP: 'CHARGE_SP',
  USE_MAGIC: 'USE_MAGIC',
  PLACE_FIELD_CARD: 'PLACE_FIELD_CARD',
  PLACE_PHASE_CARD: 'PLACE_PHASE_CARD',
  CHARGE_TO_PHASE_CARD: 'CHARGE_TO_PHASE_CARD',

  // トリガー
  ACTIVATE_TRIGGER: 'ACTIVATE_TRIGGER',

  // 特殊アクション
  DRAW_CARD: 'DRAW_CARD',
  DISCARD_CARD: 'DISCARD_CARD',
  DESTROY_MONSTER: 'DESTROY_MONSTER',
  DEAL_DAMAGE: 'DEAL_DAMAGE',
  HEAL_LIFE: 'HEAL_LIFE',
  MODIFY_MONSTER_STAT: 'MODIFY_MONSTER_STAT',

  // 勝敗判定
  CHECK_WINNER: 'CHECK_WINNER',
  SET_WINNER: 'SET_WINNER',

  // ログ
  ADD_LOG: 'ADD_LOG',

  // Phase D-4: 効果コンテキスト用
  UPDATE_PLAYER_STATE: 'UPDATE_PLAYER_STATE',  // プレイヤー状態の部分更新
  SET_GAME_FLAGS: 'SET_GAME_FLAGS',            // ゲームフラグの更新

  // 深海領域アクション
  UNLOCK_DEEP_SEA_ZONE: 'UNLOCK_DEEP_SEA_ZONE',     // 深海領域を開放
  SUBMERGE_CARD: 'SUBMERGE_CARD',                   // カードを深海領域に潜航
  SURFACE_CARD: 'SURFACE_CARD',                     // カードを深海領域から浮上
  INCREMENT_DEPTH: 'INCREMENT_DEPTH',               // 深海領域の深度を増加
  SET_DEEP_SEA_CAPACITY: 'SET_DEEP_SEA_CAPACITY',   // 深海領域の上限を設定
  DESTROY_DEEP_SEA_CARD: 'DESTROY_DEEP_SEA_CARD',   // 深海領域のカードを破壊
} as const;

export type ActionType = typeof ACTION_TYPES[keyof typeof ACTION_TYPES];

// ========================================
// アクションペイロード型定義
// ========================================

export interface ProcessPhasePayload {
  phase: PhaseType;
}

export interface SetPhasePayload {
  phase: PhaseType;
}

export interface SummonCardPayload {
  cardIndex: number;
  slotIndex: number;
}

export interface AttackPayload {
  attackerIndex: number;
  targetIndex: number;
}

export interface ExecuteSkillPayload {
  monsterIndex: number;
  skillType: 'basic' | 'advanced';
}

export interface ChargeCardPayload {
  handCardIndex: number;
  monsterIndex: number;
}

export interface ChargeSPPayload {
  monsterIndex: number;
}

export interface UseMagicPayload {
  cardIndex: number;
}

export interface DrawCardPayload {
  player: PlayerNumber;
  count?: number;
}

export interface DealDamagePayload {
  target: PlayerNumber | 'p1' | 'p2';
  amount: number;
}

export interface HealLifePayload {
  target: PlayerNumber | 'p1' | 'p2';
  amount: number;
}

export interface DestroyMonsterPayload {
  player: PlayerNumber;
  slotIndex: number;
}

export interface SetWinnerPayload {
  winner: PlayerNumber;
}

export interface PlaceFieldCardPayload {
  cardIndex: number;
}

export interface PlacePhaseCardPayload {
  cardIndex: number;
}

export interface ChargeToPhaseCardPayload {
  cardIndex: number;
}

export interface AddLogPayload {
  message: string;
  type?: LogEntry['type'];
}

export interface UpdatePlayerStatePayload {
  player: PlayerNumber;
  updates: Partial<PlayerState> | Record<string, unknown>;
}

export interface SetGameFlagsPayload {
  flags: Record<string, unknown>;
}

// 深海領域ペイロード型定義
export interface UnlockDeepSeaZonePayload {
  player: PlayerNumber;
}

export interface SubmergeCardPayload {
  player: PlayerNumber;
  cardIndex: number;
  initialDepth?: number;
}

export interface SurfaceCardPayload {
  player: PlayerNumber;
  deepSeaIndex: number;
  fieldSlot: number;
  costReduction?: number;
}

export interface IncrementDepthPayload {
  player: PlayerNumber;
  bonusDepth?: number;
}

export interface SetDeepSeaCapacityPayload {
  player: PlayerNumber;
  capacity: number;
}

export interface DestroyDeepSeaCardPayload {
  player: PlayerNumber;
  deepSeaIndex: number;
}

/** アクションオブジェクト */
export interface GameAction<T = unknown> {
  type: ActionType;
  payload: T;
}

// カードプレイアクション種別
export type CardPlayAction = 'summon' | 'special_summon' | 'magic' | 'field' | 'phasecard';

// ========================================
// アクション作成ヘルパー
// ========================================

/**
 * アクションオブジェクトを作成
 */
export function createAction<T = Record<string, unknown>>(type: ActionType, payload: T = {} as T): GameAction<T> {
  return { type, payload };
}

// よく使うアクションのショートカット
export const actions = {
  processPhase: (phase: PhaseType): GameAction<ProcessPhasePayload> =>
    createAction(ACTION_TYPES.PROCESS_PHASE, { phase }),

  nextPhase: (): GameAction<Record<string, never>> =>
    createAction(ACTION_TYPES.NEXT_PHASE, {}),

  endTurn: (): GameAction<Record<string, never>> =>
    createAction(ACTION_TYPES.END_TURN, {}),

  setPhase: (phase: PhaseType): GameAction<SetPhasePayload> =>
    createAction(ACTION_TYPES.SET_PHASE, { phase }),

  summonCard: (cardIndex: number, slotIndex: number): GameAction<SummonCardPayload> =>
    createAction(ACTION_TYPES.SUMMON_CARD, { cardIndex, slotIndex }),

  attack: (attackerIndex: number, targetIndex: number): GameAction<AttackPayload> =>
    createAction(ACTION_TYPES.ATTACK, { attackerIndex, targetIndex }),

  executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced'): GameAction<ExecuteSkillPayload> =>
    createAction(ACTION_TYPES.EXECUTE_SKILL, { monsterIndex, skillType }),

  chargeCard: (handCardIndex: number, monsterIndex: number): GameAction<ChargeCardPayload> =>
    createAction(ACTION_TYPES.CHARGE_CARD, { handCardIndex, monsterIndex }),

  chargeSP: (monsterIndex: number): GameAction<ChargeSPPayload> =>
    createAction(ACTION_TYPES.CHARGE_SP, { monsterIndex }),

  useMagic: (cardIndex: number): GameAction<UseMagicPayload> =>
    createAction(ACTION_TYPES.USE_MAGIC, { cardIndex }),

  drawCard: (player: PlayerNumber, count: number = 1): GameAction<DrawCardPayload> =>
    createAction(ACTION_TYPES.DRAW_CARD, { player, count }),

  dealDamage: (target: PlayerNumber | 'p1' | 'p2', amount: number): GameAction<DealDamagePayload> =>
    createAction(ACTION_TYPES.DEAL_DAMAGE, { target, amount }),

  healLife: (target: PlayerNumber | 'p1' | 'p2', amount: number): GameAction<HealLifePayload> =>
    createAction(ACTION_TYPES.HEAL_LIFE, { target, amount }),

  checkWinner: (): GameAction<Record<string, never>> =>
    createAction(ACTION_TYPES.CHECK_WINNER, {}),

  setWinner: (winner: PlayerNumber): GameAction<SetWinnerPayload> =>
    createAction(ACTION_TYPES.SET_WINNER, { winner }),

  // Phase B-5: フィールドカード・フェイズカード配置
  placeFieldCard: (cardIndex: number): GameAction<PlaceFieldCardPayload> =>
    createAction(ACTION_TYPES.PLACE_FIELD_CARD, { cardIndex }),

  placePhaseCard: (cardIndex: number): GameAction<PlacePhaseCardPayload> =>
    createAction(ACTION_TYPES.PLACE_PHASE_CARD, { cardIndex }),

  // フェイズカードへのチャージ（手札のカードをフェイズカードに重ねる）
  chargePhaseCard: (cardIndex: number): GameAction<ChargeToPhaseCardPayload> =>
    createAction(ACTION_TYPES.CHARGE_TO_PHASE_CARD, { cardIndex }),

  // 深海領域アクション
  submergeCard: (player: PlayerNumber, cardIndex: number, initialDepth?: number): GameAction<SubmergeCardPayload> =>
    createAction(ACTION_TYPES.SUBMERGE_CARD, { player, cardIndex, initialDepth }),

  surfaceCard: (player: PlayerNumber, deepSeaIndex: number, fieldSlot: number, costReduction?: number): GameAction<SurfaceCardPayload> =>
    createAction(ACTION_TYPES.SURFACE_CARD, { player, deepSeaIndex, fieldSlot, costReduction }),

  // Phase D-3: ログ追加
  addLog: (message: string, type: LogEntry['type'] = 'info'): GameAction<AddLogPayload> =>
    createAction(ACTION_TYPES.ADD_LOG, { message, type }),

  // Phase D-4: 効果コンテキスト用
  // プレイヤー状態の部分更新（life, deck, hand, field, graveyard, activeSP, restedSP, fieldCard, phaseCard, statusEffects, nextTurnSPBonus, magicBlocked, spReduction）
  updatePlayerState: (player: PlayerNumber, updates: Partial<PlayerState>): GameAction<UpdatePlayerStatePayload> =>
    createAction(ACTION_TYPES.UPDATE_PLAYER_STATE, { player, updates }),

  // ゲームフラグの更新（chargeUsedThisTurn等）
  setGameFlags: (flags: Record<string, unknown>): GameAction<SetGameFlagsPayload> =>
    createAction(ACTION_TYPES.SET_GAME_FLAGS, { flags }),
};

// ========================================
// メインのアクション適用関数
// ========================================

/**
 * アクションを適用して新しい状態を返す（純粋関数）
 */
export function applyAction(state: GameState, action: GameAction): GameState {
  // ゲーム終了後はアクションを受け付けない
  if (state.gameStatus === GAME_STATUS.GAME_OVER) {
    return state;
  }

  switch (action.type) {
    case ACTION_TYPES.PROCESS_PHASE:
      return applyProcessPhase(state, action.payload as ProcessPhasePayload);

    case ACTION_TYPES.NEXT_PHASE:
      return applyNextPhase(state);

    case ACTION_TYPES.END_TURN:
      return applyEndTurn(state);

    case ACTION_TYPES.SET_PHASE:
      return applySetPhase(state, action.payload as SetPhasePayload);

    case ACTION_TYPES.SUMMON_CARD:
      return applySummonCard(state, action.payload as SummonCardPayload);

    case ACTION_TYPES.ATTACK:
      return applyAttack(state, action.payload as AttackPayload);

    case ACTION_TYPES.EXECUTE_SKILL:
      return applyExecuteSkill(state, action.payload as ExecuteSkillPayload);

    case ACTION_TYPES.CHARGE_CARD:
      return applyChargeCard(state, action.payload as ChargeCardPayload);

    case ACTION_TYPES.CHARGE_SP:
      return applyChargeSP(state, action.payload as ChargeSPPayload);

    case ACTION_TYPES.USE_MAGIC:
      return applyUseMagic(state, action.payload as UseMagicPayload);

    case ACTION_TYPES.DRAW_CARD:
      return applyDrawCard(state, action.payload as DrawCardPayload);

    case ACTION_TYPES.DEAL_DAMAGE:
      return applyDealDamage(state, action.payload as DealDamagePayload);

    case ACTION_TYPES.HEAL_LIFE:
      return applyHealLife(state, action.payload as HealLifePayload);

    case ACTION_TYPES.DESTROY_MONSTER:
      return applyDestroyMonster(state, action.payload as DestroyMonsterPayload);

    case ACTION_TYPES.CHECK_WINNER:
      return applyCheckWinner(state);

    case ACTION_TYPES.SET_WINNER:
      return applySetWinner(state, action.payload as SetWinnerPayload);

    // Phase B-5: フィールドカード・フェイズカード配置
    case ACTION_TYPES.PLACE_FIELD_CARD:
      return applyPlaceFieldCard(state, action.payload as PlaceFieldCardPayload);

    case ACTION_TYPES.PLACE_PHASE_CARD:
      return applyPlacePhaseCard(state, action.payload as PlacePhaseCardPayload);

    case ACTION_TYPES.CHARGE_TO_PHASE_CARD:
      return applyChargeToPhaseCard(state, action.payload as ChargeToPhaseCardPayload);

    // Phase D-3: ログ追加
    case ACTION_TYPES.ADD_LOG: {
      const logPayload = action.payload as AddLogPayload;
      return addLog(state, logPayload.message, logPayload.type);
    }

    // Phase D-4: 効果コンテキスト用
    case ACTION_TYPES.UPDATE_PLAYER_STATE:
      return applyUpdatePlayerState(state, action.payload as UpdatePlayerStatePayload);

    case ACTION_TYPES.SET_GAME_FLAGS:
      return applySetGameFlags(state, action.payload as SetGameFlagsPayload);

    // 深海領域アクション
    case ACTION_TYPES.UNLOCK_DEEP_SEA_ZONE:
      return applyUnlockDeepSeaZone(state, action.payload as UnlockDeepSeaZonePayload);

    case ACTION_TYPES.SUBMERGE_CARD:
      return applySubmergeCard(state, action.payload as SubmergeCardPayload);

    case ACTION_TYPES.SURFACE_CARD:
      return applySurfaceCard(state, action.payload as SurfaceCardPayload);

    case ACTION_TYPES.INCREMENT_DEPTH:
      return applyIncrementDepth(state, action.payload as IncrementDepthPayload);

    case ACTION_TYPES.SET_DEEP_SEA_CAPACITY:
      return applySetDeepSeaCapacity(state, action.payload as SetDeepSeaCapacityPayload);

    case ACTION_TYPES.DESTROY_DEEP_SEA_CARD:
      return applyDestroyDeepSeaCard(state, action.payload as DestroyDeepSeaCardPayload);

    default:
      console.warn(`Unknown action type: ${action.type}`);
      return state;
  }
}

/**
 * 複数のアクションを順次適用
 */
export function applyActions(state: GameState, actionList: GameAction[]): GameState {
  return actionList.reduce((currentState, action) => {
    return applyAction(currentState, action);
  }, state);
}

// ========================================
// 個別アクション実装
// ========================================

/**
 * カードプレイ履歴を記録
 */
function recordCardPlay(state: GameState, player: PlayerNumber, card: CardWithId | CardData, action: CardPlayAction): GameState {
  const historyKey = player === 1 ? 'p1' : 'p2';
  const entry: PlayedCardEntry = {
    id: card.id,
    name: card.name,
    attribute: card.attribute,
    cost: card.cost,
    type: card.type,
    action,
  };
  return {
    ...state,
    playedCardsHistory: {
      ...state.playedCardsHistory,
      [historyKey]: [...state.playedCardsHistory[historyKey], entry],
    },
  };
}

/**
 * フェイズ処理
 */
function applyProcessPhase(state: GameState, { phase }: ProcessPhasePayload): GameState {
  switch (phase) {
    case PHASES.TURN_START:
      return processTurnStart(state);

    case PHASES.DRAW:
      return processDraw(state);

    case PHASES.MAIN:
      // メインフェイズは自動処理なし
      return state;

    case PHASES.BATTLE:
      // バトルフェイズは自動処理なし
      return state;

    case PHASES.END:
      return processEndPhase(state);

    default:
      return state;
  }
}

/**
 * ターン開始処理
 */
function processTurnStart(state: GameState): GameState {
  const currentPlayer = state.currentPlayer;
  const player = getPlayer(state, currentPlayer);
  let newState = state;

  // モンスター状態異常のターン開始処理（両プレイヤー）
  // 眠り/凍結の50%解除判定、寄生ATK減少
  ([1, 2] as PlayerNumber[]).forEach(playerNum => {
    const targetPlayer = getPlayer(newState, playerNum);
    const newField = targetPlayer.field.map(m => {
      if (!m) return null;
      const result = statusEffectEngine.processTurnStart(m);
      // 解除された状態異常のログ
      result.removedEffects.forEach(effect => {
        newState = addLog(newState, `${m.name}の${getStatusDisplayName(effect.type)}が解除された！`, 'info');
      });
      // 寄生によるATK減少ログ
      if (result.parasiteAtkReduction > 0) {
        newState = addLog(newState, `${m.name}は寄生により攻撃力が${result.parasiteAtkReduction}減少！`, 'damage');
      }
      return result.monster as MonsterInstance;
    });
    newState = updatePlayer(newState, playerNum, { field: newField });
  });

  // プレイヤーステータスのduration管理（光輝の祝福等）
  const updatedPlayer = getPlayer(newState, currentPlayer);
  const playerTurnStartResult = statusEffectEngine.processPlayerTurnStart(
    updatedPlayer.statusEffects as unknown as PlayerStatusEffect[]
  );
  if (playerTurnStartResult.expiredEffects.length > 0) {
    playerTurnStartResult.expiredEffects.forEach(effect => {
      newState = addLog(newState, `P${currentPlayer}の${getStatusDisplayName(effect.type)}が解除された！`, 'info');
    });
  }
  newState = updatePlayer(newState, currentPlayer, {
    statusEffects: playerTurnStartResult.updatedEffects as unknown as typeof updatedPlayer.statusEffects,
  });

  // 最新のプレイヤー状態を再取得
  const playerAfterStatus = getPlayer(newState, currentPlayer);

  // SP回復（レスト→アクティブ）
  const totalSP = playerAfterStatus.activeSP + playerAfterStatus.restedSP;
  let newSP = Math.min(totalSP + 1, MAX_SP); // 1SP増加、最大10

  // SPボーナス適用
  if (playerAfterStatus.nextTurnSPBonus > 0) {
    newSP = Math.min(newSP + playerAfterStatus.nextTurnSPBonus, MAX_SP);
  }

  // SP減少適用（壮麗効果 + ステータス効果を合算）
  const legacySpReduction = playerAfterStatus.spReduction || 0;
  const statusSpReduction = statusEffectEngine.getSPGainReduction(playerAfterStatus.statusEffects);
  const totalSpReduction = legacySpReduction + statusSpReduction;
  if (totalSpReduction > 0) {
    newSP = Math.max(1, newSP - totalSpReduction);
  }

  // モンスターの攻撃可能フラグをON & 技発動済みフラグリセット
  const currentField = getPlayer(newState, currentPlayer).field;
  const newField = currentField.map((monster, slotIndex) => {
    if (monster === null || monster === undefined) return null;
    if (!monster.name) {
      console.warn('[GameActions ターン開始] フィールドにnameがないモンスターがいます:', {
        slotIndex,
        monsterKeys: Object.keys(monster),
        monster,
      });
      return null; // 不正なオブジェクトはnullに置き換え
    }
    return {
      ...monster,
      canAttack: true,
      usedSkillThisTurn: false,
    };
  });

  // フィールドカードの使用済みフラグをリセット（ルミナスの聖域など）
  const currentFieldCard = getPlayer(newState, currentPlayer).fieldCard;
  const updatedFieldCard = currentFieldCard
    ? { ...currentFieldCard, usedDestroyPreventionThisTurn: false }
    : null;

  // SP_GAIN_REDUCTION ステータス効果を解除
  let newStatusEffects = getPlayer(newState, currentPlayer).statusEffects;
  if (statusSpReduction > 0) {
    newStatusEffects = statusEffectEngine.removeSPGainReduction(newStatusEffects);
  }

  // プレイヤー状態更新
  newState = updatePlayer(newState, currentPlayer, {
    activeSP: newSP,
    restedSP: 0,
    nextTurnSPBonus: 0,
    spReduction: 0,
    magicBlocked: false, // 魔法ブロック解除
    field: newField,
    fieldCard: updatedFieldCard,
    statusEffects: newStatusEffects,
  });

  // ターンフラグリセット
  newState = updateTurnFlags(newState, {
    chargeUsedThisTurn: false,
  });

  // C0000313 アクア・リヴァイアサン上級技: 対象プレイヤーのターン開始時にSP1つをレスト状態にする
  if (state.turnFlags.pendingOpponentSPRest === currentPlayer) {
    const currentActiveSP = getPlayer(newState, currentPlayer).activeSP;
    if (currentActiveSP > 0) {
      newState = updatePlayer(newState, currentPlayer, {
        activeSP: currentActiveSP - 1,
        restedSP: getPlayer(newState, currentPlayer).restedSP + 1,
      });
      newState = addLog(newState, `アクア・リヴァイアサンの効果: P${currentPlayer}のSP1個がレスト状態に！`, 'damage');
    } else {
      newState = addLog(newState, `アクア・リヴァイアサンの効果: SPがないため効果なし`, 'info');
    }
    // フラグをクリア
    newState = updateTurnFlags(newState, { pendingOpponentSPRest: null });
  }

  // SP減少があった場合のログ
  if (totalSpReduction > 0) {
    const reason = statusSpReduction > 0 ? '【深淵の呪縛】効果' : '【壮麗】効果';
    newState = addLog(newState, `P${currentPlayer}: SP増加減少（${reason}）`, 'damage');
  }

  // C0000314 聖光フェニックス復活処理
  {
    const playerState = getPlayer(newState, currentPlayer);
    const graveyard = playerState.graveyard;
    const field = playerState.field;

    // 復活待ちカードを探す（このプレイヤーがオーナーの場合のみ）
    const cardToReviveIndex = graveyard.findIndex(card => {
      const pendingRevival = (card as CardWithId & { pendingRevival?: { owner: PlayerNumber; attack?: number } }).pendingRevival;
      return pendingRevival && pendingRevival.owner === currentPlayer;
    });

    if (cardToReviveIndex !== -1) {
      const cardToRevive = graveyard[cardToReviveIndex] as CardWithId & { pendingRevival?: { owner: PlayerNumber; attack?: number } };
      const revivalInfo = cardToRevive.pendingRevival!;

      // 空きスロットを探す
      const emptySlot = field.findIndex(slot => slot === null);

      if (emptySlot !== -1) {
        // 復活処理
        const revivedAttack = revivalInfo.attack || cardToRevive.attack || 0;
        const revivedHp = cardToRevive.hp || 0;
        const summoningSickness = state.config?.summoningSickness ?? true;

        const revivedMonster: MonsterInstance = {
          ...cardToRevive,
          attack: revivedAttack,
          currentAttack: revivedAttack,
          hp: revivedHp,
          currentHp: revivedHp,
          maxHp: revivedHp,
          canAttack: !summoningSickness,
          usedSkillThisTurn: false,
          charges: [],
          statusEffects: [],
          phoenixRevived: true,
          owner: currentPlayer,
          pendingRevival: undefined,
        };

        // フィールドに配置
        const newFieldWithPhoenix = [...field];
        newFieldWithPhoenix[emptySlot] = revivedMonster;

        // 墓地からカードを除去
        const newGraveyardWithoutPhoenix = graveyard.filter((_, i) => i !== cardToReviveIndex);

        newState = updatePlayer(newState, currentPlayer, {
          field: newFieldWithPhoenix,
          graveyard: newGraveyardWithoutPhoenix,
        });

        newState = addLog(newState, `聖光フェニックスが攻撃力${revivedAttack}で復活！`, 'heal');

        // トリガー登録
        newState = registerCardTriggersPure(newState, revivedMonster, currentPlayer, emptySlot);
      } else {
        newState = addLog(newState, '聖光フェニックスの復活: フィールドに空きがありません', 'info');
        // フラグをクリア（復活できなかったが待機も終了）
        const newGraveyardCleaned = graveyard.map((c, i) => {
          if (i === cardToReviveIndex) {
            const { pendingRevival, ...rest } = c as CardWithId & { pendingRevival?: unknown };
            return { ...rest, phoenixRevived: true } as CardWithId;
          }
          return c;
        });
        newState = updatePlayer(newState, currentPlayer, { graveyard: newGraveyardCleaned });
      }
    }
  }

  newState = addLog(newState, `ターン${state.turn}: P${currentPlayer}のターン開始`, 'info');

  return newState;
}

/**
 * ドローフェイズ処理
 */
function processDraw(state: GameState): GameState {
  const currentPlayer = state.currentPlayer;
  const player = getPlayer(state, currentPlayer);

  // デッキが空ならドローしない
  if (player.deck.length === 0) {
    return addLog(state, 'デッキが空です', 'info');
  }

  // 1枚ドロー
  const [drawnCard, ...remainingDeck] = player.deck;
  const newHand = [...player.hand, drawnCard];

  let newState = updatePlayer(state, currentPlayer, {
    deck: remainingDeck,
    hand: newHand,
  });

  newState = addLog(newState, `${drawnCard.name}をドロー`, 'info');

  return newState;
}

/**
 * エンドフェイズ処理
 */
function processEndPhase(state: GameState): GameState {
  const currentPlayer = state.currentPlayer;
  const player = getPlayer(state, currentPlayer);
  let newState = state;

  // モンスター状態異常のエンドフェイズ処理（両プレイヤー）
  // duration: 0 の状態異常（ターン終了時まで）を解除、深蝕のATK減少等
  ([1, 2] as PlayerNumber[]).forEach(playerNum => {
    const targetPlayer = getPlayer(newState, playerNum);
    const newField = targetPlayer.field.map(m => {
      if (!m) return null;
      const result = statusEffectEngine.processEndPhase(m);
      // 解除された状態異常のログ
      result.removedEffects.forEach(effect => {
        newState = addLog(newState, `${m.name}の${getStatusDisplayName(effect.type)}が解除された！`, 'info');
      });
      // 深蝕による攻撃力減少ログ（そのモンスターのオーナーのターンのみ）
      if (result.atkReduction > 0 && playerNum === currentPlayer) {
        newState = addLog(newState, `${m.name}は深蝕により攻撃力が${result.atkReduction}減少！`, 'damage');
      }
      return result.monster as MonsterInstance;
    });
    newState = updatePlayer(newState, playerNum, { field: newField });
  });

  // プレイヤーステータスによるエンドフェイズ処理（POISON等）
  // 最新のプレイヤー状態を再取得
  const updatedPlayer = getPlayer(newState, currentPlayer);
  const { damage } = statusEffectEngine.processPlayerEndPhase(updatedPlayer.statusEffects);
  if (damage > 0) {
    const newLife = Math.max(0, updatedPlayer.life - damage);
    newState = updatePlayer(newState, currentPlayer, { life: newLife });
    newState = addLog(newState, `毒により${damage}ダメージ！`, 'damage');

    // ライフ0チェック
    const winner = checkWinner(newState);
    if (winner !== null) {
      newState = updateGameProgress(newState, {
        winner,
        gameStatus: GAME_STATUS.GAME_OVER,
      });
    }
  }

  // ダイレクトアタック禁止の解除
  const playerForDirectAttack = getPlayer(newState, currentPlayer);
  if (!statusEffectEngine.canDirectAttack(playerForDirectAttack.statusEffects)) {
    const updatedStatusEffects = statusEffectEngine.removeNoDirectAttack(playerForDirectAttack.statusEffects);
    newState = updatePlayer(newState, currentPlayer, { statusEffects: updatedStatusEffects });
    newState = addLog(newState, `P${currentPlayer}のダイレクトアタック禁止が解除された`, 'info');
  }

  // ターン終了時に破壊されるモンスターを処理（荒海の強襲、コントロール奪取等）
  const currentField = getPlayer(newState, currentPlayer).field;
  const toDestroy = currentField.filter((m): m is MonsterInstance =>
    m !== null && m.destroyAtEndOfTurn === true
  );

  if (toDestroy.length > 0) {
    // damageOwnerOnDestroyによるダメージ計算
    let totalDamage = 0;
    toDestroy.forEach(m => {
      if (m.damageOwnerOnDestroy && m.damageOwnerOnDestroy > 0) {
        totalDamage += m.damageOwnerOnDestroy;
        newState = addLog(newState, `${m.name}の破壊により${m.damageOwnerOnDestroy}ダメージ！`, 'damage');
      }
    });

    // フィールドから除去
    const newField = currentField.map(m => {
      if (m && m.destroyAtEndOfTurn) {
        newState = addLog(newState, `${m.name}はターン終了時に破壊された！`, 'damage');
        return null;
      }
      return m;
    });

    // 墓地に送る（destroyAtEndOfTurn, damageOwnerOnDestroyフラグを除去）
    const currentGraveyard = getPlayer(newState, currentPlayer).graveyard;
    const newGraveyard = [
      ...currentGraveyard,
      ...toDestroy.map(m => {
        const { destroyAtEndOfTurn, damageOwnerOnDestroy, ...cardData } = m;
        return cardData as CardWithId;
      })
    ];

    // ダメージを適用
    const currentLife = getPlayer(newState, currentPlayer).life;
    const newLife = Math.max(0, currentLife - totalDamage);

    newState = updatePlayer(newState, currentPlayer, {
      field: newField,
      graveyard: newGraveyard,
      life: totalDamage > 0 ? newLife : currentLife,
    });
  }

  // 【魂結】リンクダメージ処理（自分フィールドのモンスターが持つlinkedEndPhaseDamageを相手に与える）
  {
    const myField = getPlayer(newState, currentPlayer).field;
    let linkTotalDamage = 0;

    myField.forEach(monster => {
      if (monster && monster.linkedEndPhaseDamage && monster.linkedEndPhaseDamage.length > 0) {
        monster.linkedEndPhaseDamage.forEach(({ damage }) => {
          linkTotalDamage += damage;
          newState = addLog(newState, `${monster.name}の【魂結】効果: 相手に${damage}ダメージ`, 'damage');
        });
      }
    });

    if (linkTotalDamage > 0) {
      const opponentPlayer = getOpponentNumber(currentPlayer);
      const opponentLife = getPlayer(newState, opponentPlayer).life;
      const newOpponentLife = Math.max(0, opponentLife - linkTotalDamage);
      newState = updatePlayer(newState, opponentPlayer, { life: newOpponentLife });

      // 勝敗チェック
      const winner = checkWinner(newState);
      if (winner !== null) {
        newState = updateGameProgress(newState, {
          winner,
          gameStatus: GAME_STATUS.GAME_OVER,
        });
      }
    }
  }

  // 棘根の罠等によるターン終了時ダメージ処理（相手フィールドのthornTrapDamageフラグ）
  {
    const opponentPlayer = getOpponentNumber(currentPlayer);
    const opponentField = getPlayer(newState, opponentPlayer).field;
    let thornTotalDamage = 0;

    // 対象モンスターを探してダメージを計算
    opponentField.forEach(m => {
      if (m && m.thornTrapDamage) {
        thornTotalDamage += m.thornTrapDamage;
        newState = addLog(newState, `棘根の罠の効果: ${m.thornTrapDamage}ダメージ！`, 'damage');
      }
    });

    // ダメージを与える
    if (thornTotalDamage > 0) {
      const opponentLife = getPlayer(newState, opponentPlayer).life;
      const newOpponentLife = Math.max(0, opponentLife - thornTotalDamage);
      newState = updatePlayer(newState, opponentPlayer, { life: newOpponentLife });

      // 勝敗チェック
      const winner = checkWinner(newState);
      if (winner !== null) {
        newState = updateGameProgress(newState, {
          winner,
          gameStatus: GAME_STATUS.GAME_OVER,
        });
      }
    }

    // フラグをクリア
    const cleanedOpponentField = opponentField.map(m => {
      if (m && m.thornTrapDamage) {
        const { thornTrapDamage, ...rest } = m;
        return rest as MonsterInstance;
      }
      return m;
    });
    newState = updatePlayer(newState, opponentPlayer, { field: cleanedOpponentField });
  }

  // ON_OPPONENT_END_PHASEトリガーを発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_OPPONENT_END_PHASE, {});

  // 墓地カードのON_END_PHASE_FROM_GRAVEYARDトリガーを発火
  {
    const currentGraveyardForTriggers = getPlayer(newState, currentPlayer).graveyard;
    currentGraveyardForTriggers.forEach((card) => {
      // カードのトリガーをパース
      const cardTriggers = parseCardTriggers(card);
      cardTriggers.forEach((trigger) => {
        if (trigger.type === TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD &&
            trigger.activationType === ACTIVATION_TYPES.AUTOMATIC &&
            typeof trigger.effect === 'function') {
          try {
            const { createPureContext } = require('./contextAdapter');
            const context = createPureContext(newState, {
              card,
              currentPlayer,
            });
            trigger.effect(context);
            newState = context.getState();
          } catch (error) {
            console.error(`墓地トリガー発動エラー: ${card.name}`, error);
          }
        }
      });
    });
  }

  // 寄生効果の無効化解除処理（相手エンドフェイズで効果無効化が終了）
  ([1, 2] as PlayerNumber[]).forEach(playerNum => {
    const targetPlayer = getPlayer(newState, playerNum);
    const parasiteProcessedField = targetPlayer.field.map(m => {
      if (!m) return null;
      const result = statusEffectEngine.processOpponentEndPhase(m, currentPlayer);
      if (result.parasiteEffectNegationRemoved) {
        newState = addLog(newState, `${m.name}の効果無効化が解除された（寄生効果）`, 'info');
      }
      return result.monster as MonsterInstance;
    });
    newState = updatePlayer(newState, playerNum, { field: parasiteProcessedField });
  });

  // 深海領域の深度増加処理（自分のエンドフェイズ時）
  {
    const myPlayerState = getPlayer(newState, currentPlayer);
    const myDeepSeaZone = myPlayerState.deepSeaZone;
    const myField = myPlayerState.field;

    // 深海領域が開放されていてカードがある場合のみ処理
    if (myDeepSeaZone?.isUnlocked && myDeepSeaZone.cards.length > 0) {
      // 【暗黒水域】を持つモンスターがフィールドにいる場合、追加深度+1
      const hasAnkokuSuiiki = myField.some(m => m && hasAnkokuSuiikiKeyword(m as CardWithId));
      const bonusDepth = hasAnkokuSuiiki ? 1 : 0;

      const result = incrementDepth(myDeepSeaZone, bonusDepth);

      if (result.incrementedCount > 0) {
        newState = updatePlayer(newState, currentPlayer, { deepSeaZone: result.updatedZone });
        if (bonusDepth > 0) {
          newState = addLog(newState, `深海領域: ${result.incrementedCount}体の深度が2増加（【暗黒水域】効果）`, 'info');
        } else {
          newState = addLog(newState, `深海領域: ${result.incrementedCount}体の深度が1増加`, 'info');
        }
      }
    }
  }

  // ターン終了時までの効果をリセット（両プレイヤー）
  // attackBuffUntilEndOfTurn, tempAttackReduction, indestructibleUntilEndOfTurn,
  // noCounterDamage, counterDamageRate, tempCostModifier 等
  ([1, 2] as PlayerNumber[]).forEach(playerNum => {
    const targetPlayer = getPlayer(newState, playerNum);
    const cleanedField = targetPlayer.field.map(m => {
      if (!m) return null;
      let updated = { ...m };
      let hasChanges = false;

      const withoutAttackModifiers = clearTemporaryAttack(updated);
      if (withoutAttackModifiers !== updated) {
        updated = withoutAttackModifiers;
        hasChanges = true;
        newState = addLog(newState, `${m.name}の一時的な攻撃力補正が終了（${updated.currentAttack}）`, 'info');
      }

      // 破壊耐性をリセット
      if (updated.indestructibleUntilEndOfTurn) {
        delete (updated as Record<string, unknown>).indestructibleUntilEndOfTurn;
        hasChanges = true;
      }

      // 反撃ダメージ無効をリセット
      if (updated.noCounterDamage) {
        delete (updated as Record<string, unknown>).noCounterDamage;
        hasChanges = true;
      }

      // 反撃ダメージ倍率をリセット
      if (updated.counterDamageRate !== undefined) {
        delete (updated as Record<string, unknown>).counterDamageRate;
        hasChanges = true;
      }

      return hasChanges ? updated : m;
    });

    // 手札のtempCostModifierをリセット
    const cleanedHand = targetPlayer.hand.map(card => {
      if (card.tempCostModifierUntilEndPhase) {
        const { tempCostModifier, tempCostModifierSource, tempCostModifierUntilEndPhase, ...rest } = card;
        return rest as CardWithId;
      }
      return card;
    });

    newState = updatePlayer(newState, playerNum, {
      field: cleanedField,
      hand: cleanedHand,
    });
  });

  return newState;
}

/**
 * 次のフェイズへ進む
 */
function applyNextPhase(state: GameState): GameState {
  const nextPhase = (state.phase + 1) as PhaseType;

  if (nextPhase > PHASES.END) {
    // ターン終了 → 相手ターンへ
    return applyEndTurn(state);
  }

  return updateGameProgress(state, { phase: nextPhase });
}

/**
 * フェイズを直接設定（Phase B: UI統合用）
 */
function applySetPhase(state: GameState, { phase }: SetPhasePayload): GameState {
  if (phase < PHASES.TURN_START || phase > PHASES.END) {
    console.warn(`Invalid phase: ${phase}`);
    return state;
  }
  return updateGameProgress(state, { phase });
}

/**
 * ターン終了
 */
function applyEndTurn(state: GameState): GameState {
  const nextPlayer = getOpponentNumber(state.currentPlayer);
  const nextTurn = nextPlayer === state.firstPlayer ? state.turn + 1 : state.turn;
  // isFirstTurnは後攻プレイヤーのターン終了時（次が先攻プレイヤー）に解除
  const nextIsFirstTurn = nextPlayer === state.firstPlayer ? false : state.isFirstTurn;

  return updateGameProgress(state, {
    currentPlayer: nextPlayer,
    turn: nextTurn,
    phase: PHASES.TURN_START,
    isFirstTurn: nextIsFirstTurn,
  });
}

/**
 * カード召喚
 */
function applySummonCard(state: GameState, { cardIndex, slotIndex }: SummonCardPayload): GameState {
  const currentPlayer = state.currentPlayer;
  const player = getPlayer(state, currentPlayer);
  const baseCard = player.hand[cardIndex];

  // バリデーション
  if (!baseCard) {
    return addLog(state, 'カードが見つかりません', 'info');
  }

  if (!canSummonCard(state, baseCard, slotIndex)) {
    return addLog(state, '召喚できません', 'info');
  }

  // コスト軽減プロパティを持つカードとしてアクセス
  const card = baseCard as CardWithTempModifier;

  // モンスターインスタンス生成
  const monster = createMonsterInstance(card);
  monster.owner = currentPlayer;
  monster.canAttack = !(state.config?.summoningSickness ?? true); // 召喚酔い設定に依存

  // 召喚時バフを適用（フィールドカードによる常時効果等）
  const summonBuffs = getSummonBuffsPure(state, monster, currentPlayer);
  if (summonBuffs.atkBuff > 0 || summonBuffs.hpBuff > 0) {
    if (summonBuffs.atkBuff > 0) {
      monster.currentAttack = (monster.currentAttack || monster.attack || 0) + summonBuffs.atkBuff;
    }
    if (summonBuffs.hpBuff > 0) {
      monster.currentHp = (monster.currentHp || monster.hp || 0) + summonBuffs.hpBuff;
      monster.maxHp = (monster.maxHp || monster.hp || 0) + summonBuffs.hpBuff;
    }
  }

  // 「次の1体のみ」のコスト軽減を使用した場合の情報を保存
  const usedOneTimeModifier = card.tempCostModifierOneTime && card.tempCostModifier;
  const oneTimeSource = usedOneTimeModifier ? card.tempCostModifierSource : null;

  // 手札からカードを削除し、同じソースの「次の1体のみ」軽減をリセット
  let newHand: CardWithId[] = player.hand.filter((_, i) => i !== cardIndex);
  if (oneTimeSource) {
    newHand = newHand.map(c => {
      const tc = c as CardWithTempModifier;
      if (tc.tempCostModifierOneTime && tc.tempCostModifierSource === oneTimeSource) {
        // tempCostModifier関連のプロパティを削除
        const { tempCostModifier, tempCostModifierSource, tempCostModifierOneTime, ...rest } = tc;
        return rest as CardWithId;
      }
      return c;
    });
  }

  // フィールドにモンスターを配置
  const newField = [...player.field];
  newField[slotIndex] = monster;

  // SPを消費（tempCostModifier + プレイヤーステータス軽減を考慮、activeSP優先、足りなければbonusSP使用）
  const actualCost = getSummonCost(state, card, currentPlayer);
  const bonusSP = player.bonusSP || 0;
  const fromActive = Math.min(player.activeSP, actualCost);
  const fromBonus = actualCost - fromActive;
  const newActiveSP = player.activeSP - fromActive;
  const newRestedSP = player.restedSP + fromActive; // レスト化はactiveSP分のみ
  const newBonusSP = bonusSP - fromBonus;

  // 状態更新
  let newState = updatePlayer(state, currentPlayer, {
    hand: newHand,
    field: newField,
    activeSP: newActiveSP,
    restedSP: newRestedSP,
    bonusSP: newBonusSP,
  });

  if (fromBonus > 0) {
    newState = addLog(newState, `☆ボーナスSPを${fromBonus}使用！`, 'heal');
  }
  // コスト軽減があった場合は元のコストも表示
  const hasCostReduction = actualCost !== card.cost;
  const costMessage = hasCostReduction
    ? `${card.name}を召喚！ (コスト: ${card.cost}→${actualCost}SP)`
    : `${card.name}を召喚！ (コスト: ${actualCost}SP)`;
  newState = addLog(newState, costMessage, 'info');

  // カードプレイ履歴を記録
  newState = recordCardPlay(newState, currentPlayer, card, 'summon');

  // トリガー登録
  newState = registerCardTriggersPure(newState, monster, currentPlayer, slotIndex);

  // ON_SUMMONトリガー発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SUMMON, {
    card: monster,
    slotIndex,
  });

  return newState;
}

/**
 * 攻撃処理
 */
function applyAttack(state: GameState, { attackerIndex, targetIndex }: AttackPayload): GameState {
  const currentPlayer = state.currentPlayer;
  const opponentNumber = getOpponentNumber(currentPlayer);
  const player = getPlayer(state, currentPlayer);
  const opponent = getOpponent(state, currentPlayer);

  const attacker = player.field[attackerIndex];

  // バリデーション
  if (!attacker || !canMonsterAttack(attacker)) {
    return addLog(state, 'このモンスターは攻撃できません', 'damage');
  }

  // 先攻1ターン目は攻撃不可
  if (state.isFirstTurn && state.currentPlayer === state.firstPlayer) {
    return addLog(state, '先攻1ターン目は攻撃できません', 'info');
  }

  let newState = state;

  if (targetIndex === -1) {
    // ダイレクトアタック
    // プレイヤーステータスによるダイレクトアタック禁止をチェック
    if (!statusEffectEngine.canDirectAttack(player.statusEffects)) {
      return addLog(state, 'このターン中はダイレクトアタックできません！', 'damage');
    }
    newState = executeDirectAttack(newState, attacker, attackerIndex, currentPlayer);
  } else {
    // モンスターへの攻撃
    const target = opponent.field[targetIndex];
    if (!target) {
      return addLog(state, '攻撃対象が存在しません', 'damage');
    }
    newState = executeMonsterAttack(newState, attacker, target, attackerIndex, targetIndex, currentPlayer);
  }

  // 攻撃済みフラグを設定
  const playerData = getPlayer(newState, currentPlayer);
  const updatedField = [...playerData.field];
  if (updatedField[attackerIndex]) {
    updatedField[attackerIndex] = {
      ...updatedField[attackerIndex],
      canAttack: false,
    };
  }
  newState = updatePlayer(newState, currentPlayer, { field: updatedField });

  // 勝敗判定
  newState = applyCheckWinner(newState);

  return newState;
}

/**
 * ダイレクトアタック実行
 */
function executeDirectAttack(state: GameState, attacker: MonsterInstance, attackerIndex: number, currentPlayer: PlayerNumber): GameState {
  const opponentNumber = getOpponentNumber(currentPlayer);
  const player = getPlayer(state, currentPlayer);
  const opponent = getOpponent(state, currentPlayer);

  // 飛行/対空システムによるダイレクトアタック可否を判定
  const directStatus = getDirectAttackStatus(attacker, opponent.field);
  if (!directStatus.canDirect) {
    return addLog(state, directStatus.reason, 'damage');
  }

  // 【彷徨い】状態の場合、攻撃力は0になる
  const rawEffectiveAttack = attacker.currentAttack ?? attacker.attack ?? 0;
  let effectiveAttack = statusEffectEngine.isAttackZeroed(attacker) ? 0 : rawEffectiveAttack;

  // 【孤軍奮闘】自分のモンスターが相手より少ない場合の補正
  const loneWarriorEnabled = state.config?.loneWarriorRule ?? true;
  if (loneWarriorEnabled) {
    const myMonsterCount = player.field.filter(m => m !== null).length;
    const opponentMonsterCount = opponent.field.filter(m => m !== null).length;
    const countDiff = opponentMonsterCount - myMonsterCount;

    if (countDiff > 0) {
      const bonusLevel = Math.min(countDiff, 3); // 最大3体差
      const bonus = LONE_WARRIOR_BONUS[bonusLevel];
      const atkIncrease = Math.floor(effectiveAttack * bonus.atkBonus);
      effectiveAttack += atkIncrease;
    }
  }

  let damage = effectiveAttack;

  // 飛行/対空システムによるダメージ軽減
  if (directStatus.damageMultiplier < 1) {
    damage = Math.floor(damage * directStatus.damageMultiplier);
  }

  // フィールドカードがある場合は75%に軽減
  if (opponent.fieldCard) {
    damage = Math.floor(damage * 0.75);
  }

  let newState = state;

  // 飛行/対空による理由を表示
  if (directStatus.damageMultiplier < 1) {
    newState = addLog(newState, directStatus.reason, 'info');
  }

  newState = addLog(newState, `${attacker.name}のダイレクトアタック！ ${damage}ダメージ！`, 'damage');

  // 地上→飛行のみの場合、飛行モンスターからの反撃ダメージを計算
  const flightCounterDamage = calculateFlightCounterDamage(opponent.field, directStatus.counterMultiplier);
  if (flightCounterDamage > 0) {
    newState = addLog(newState, `【飛行】飛行モンスターからの反撃で${attacker.name}に${flightCounterDamage}ダメージ！`, 'damage');

    // 攻撃者のHP減少
    const attackerHP = attacker.currentHp ?? attacker.hp ?? 0;
    const newAttackerHP = attackerHP - flightCounterDamage;

    const playerData = getPlayer(newState, currentPlayer);
    const updatedPlayerField = [...playerData.field];

    if (newAttackerHP <= 0) {
      // 攻撃者が破壊
      updatedPlayerField[attackerIndex] = null;
      const newGraveyard = [...playerData.graveyard, attacker];
      newState = updatePlayer(newState, currentPlayer, {
        field: updatedPlayerField,
        graveyard: newGraveyard,
      });
      newState = addLog(newState, `${attacker.name}は飛行モンスターの反撃で破壊された！`, 'damage');

      // 破壊時トリガー発火
      newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_DESTROY_SELF, {
        destroyedCard: attacker,
        slotIndex: attackerIndex,
      });
    } else {
      // HPを減少
      updatedPlayerField[attackerIndex] = {
        ...attacker,
        currentHp: newAttackerHP,
      };
      newState = updatePlayer(newState, currentPlayer, { field: updatedPlayerField });
    }
  }

  // ダイレクトアタック時の攻撃トリガーを発火
  const attackContext = {
    attacker,
    attackerIndex,
    target: null,
    targetIndex: -1,
    damage,
    isDirect: true,
    accumulatedDamageBonus: 0, // 攻撃時トリガーで蓄積されるダメージボーナス
  };
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_ATTACK, attackContext);

  // 攻撃時トリガーによるボーナスを加算
  const finalDamage = damage + (attackContext.accumulatedDamageBonus || 0);
  const newLife = Math.max(0, opponent.life - finalDamage);
  newState = updatePlayer(newState, opponentNumber, { life: newLife });

  return newState;
}

/**
 * モンスターへの攻撃実行
 */
function executeMonsterAttack(state: GameState, attacker: MonsterInstance, target: MonsterInstance, attackerIndex: number, targetIndex: number, currentPlayer: PlayerNumber): GameState {
  const opponentNumber = getOpponentNumber(currentPlayer);
  const player = getPlayer(state, currentPlayer);
  const opponent = getOpponent(state, currentPlayer);
  // 【彷徨い】状態の場合、攻撃力は0になる
  const rawAttackPower = attacker.currentAttack ?? attacker.attack ?? 0;
  const baseAttackPower = statusEffectEngine.isAttackZeroed(attacker) ? 0 : rawAttackPower;
  const targetHP = target.currentHp ?? target.hp ?? 0;

  // 飛行/対空による攻撃対象制限チェック
  const validTargets = getValidAttackTargets(attacker, opponent.field);
  if (!validTargets.includes(targetIndex)) {
    return addLog(state, `${attacker.name}は${target.name}を攻撃できません（飛行モンスターには対空が必要）`, 'damage');
  }

  // 【孤軍奮闘】補正計算
  let loneWarriorAtkBonus = 0;
  let loneWarriorDamageReduction = 0;
  const loneWarriorEnabled = state.config?.loneWarriorRule ?? true;
  if (loneWarriorEnabled) {
    const myMonsterCount = player.field.filter(m => m !== null).length;
    const opponentMonsterCount = opponent.field.filter(m => m !== null).length;
    const countDiff = opponentMonsterCount - myMonsterCount;

    if (countDiff > 0) {
      const bonusLevel = Math.min(countDiff, 3); // 最大3体差
      const bonus = LONE_WARRIOR_BONUS[bonusLevel];
      loneWarriorAtkBonus = bonus.atkBonus;
      loneWarriorDamageReduction = bonus.damageReduction;
    }
  }

  // 孤軍奮闘による攻撃力補正
  const attackPower = baseAttackPower + Math.floor(baseAttackPower * loneWarriorAtkBonus);

  let newState = addLog(state, `${attacker.name}が${target.name}を攻撃！`, 'info');

  // 攻撃トリガーを発火
  const attackContext = {
    attacker,
    attackerIndex,
    target,
    targetIndex,
    damage: attackPower,
    accumulatedDamageBonus: 0, // 攻撃時トリガーで蓄積されるダメージボーナス
  };
  // 攻撃者のON_ATTACKトリガー
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_ATTACK, attackContext);
  // 攻撃対象のON_ATTACKEDトリガー（相手視点で発火）
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_ATTACKED, { ...attackContext, card: target });
  // 相手のON_OPPONENT_ATTACKトリガー
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_OPPONENT_ATTACK, { ...attackContext, card: target });

  // ダメージ計算（攻撃時トリガーによるボーナスを加算）
  const damage = attackPower + (attackContext.accumulatedDamageBonus || 0);
  const newTargetHP = targetHP - damage;

  // カウンターダメージ - 飛行/対空による倍率計算
  const targetAttack = target.currentAttack ?? target.attack ?? 0;
  const flightCounterRate = getCounterAttackMultiplier(attacker, target);
  const baseCounterDamage = Math.floor(targetAttack * flightCounterRate);
  // 孤軍奮闘による軽減適用
  const counterDamage = Math.floor(baseCounterDamage * (1 - loneWarriorDamageReduction));
  const attackerHP = attacker.currentHp ?? attacker.hp ?? 0;
  const newAttackerHP = attackerHP - counterDamage;

  // 飛行→地上の場合、反撃なし（【飛行】キーワードまたは【彷徨い】状態）
  if (flightCounterRate === 0 && hasFlightEffect(attacker) && isGroundMonster(target)) {
    newState = addLog(newState, `【飛行】${attacker.name}は地上モンスターからの反撃を受けない！`, 'info');
  }

  // 攻撃側の更新
  const playerData = getPlayer(newState, currentPlayer);
  const updatedPlayerField = [...playerData.field];

  if (newAttackerHP <= 0) {
    // 攻撃側が破壊
    updatedPlayerField[attackerIndex] = null;
    const newGraveyard = [...playerData.graveyard, attacker];
    newState = updatePlayer(newState, currentPlayer, {
      field: updatedPlayerField,
      graveyard: newGraveyard,
    });
    newState = addLog(newState, `${attacker.name}は反撃で破壊された！`, 'damage');

    // 破壊時トリガー発火
    newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_DESTROY_SELF, {
      destroyedCard: attacker,
      slotIndex: attackerIndex,
    });

    // トリガー解除
    const { unregisterCardTriggersPure } = require('./triggerEnginePure');
    newState = unregisterCardTriggersPure(newState, attacker.uniqueId);
  } else {
    // 攻撃側HP減少
    updatedPlayerField[attackerIndex] = {
      ...attacker,
      currentHp: newAttackerHP,
    };
    newState = updatePlayer(newState, currentPlayer, { field: updatedPlayerField });
  }

  // 防御側の更新
  const opponentData = getPlayer(newState, opponentNumber);
  const updatedOpponentField = [...opponentData.field];

  if (newTargetHP <= 0) {
    // 防御側が破壊
    updatedOpponentField[targetIndex] = null;
    const newGraveyard = [...opponentData.graveyard, target];
    newState = updatePlayer(newState, opponentNumber, {
      field: updatedOpponentField,
      graveyard: newGraveyard,
    });
    newState = addLog(newState, `${target.name}を破壊！`, 'damage');

    // 【貫通ダメージ】余剰ダメージの50%を相手ライフに与える
    const piercingEnabled = newState.config?.piercingDamage ?? true;
    if (piercingEnabled) {
      const excessDamage = damage - targetHP;
      const piercingDmgValue = Math.floor(excessDamage * PIERCING_DAMAGE_RATE);
      if (piercingDmgValue > 0) {
        const newOpponentLife = Math.max(0, opponentData.life - piercingDmgValue);
        newState = updatePlayer(newState, opponentNumber, { life: newOpponentLife });
        newState = addLog(newState, `【貫通ダメージ】余剰${excessDamage}の${Math.floor(PIERCING_DAMAGE_RATE * 100)}%→${piercingDmgValue}ダメージ！`, 'damage');
      }
    }

    // 破壊時トリガー発火
    newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_DESTROY_SELF, {
      destroyedCard: target,
      slotIndex: targetIndex,
    });

    // トリガー解除
    const { unregisterCardTriggersPure } = require('./triggerEnginePure');
    newState = unregisterCardTriggersPure(newState, target.uniqueId);
  } else {
    // 防御側HP減少
    updatedOpponentField[targetIndex] = {
      ...target,
      currentHp: newTargetHP,
    };
    newState = updatePlayer(newState, opponentNumber, { field: updatedOpponentField });
  }

  newState = addLog(newState, `${attacker.name}が${target.name}に攻撃！ ${damage}ダメージ（反撃${counterDamage}）`, 'damage');

  return newState;
}

/**
 * 技発動（contextアダプター統合版）
 * effectEngineと連携して完全な技効果を実行
 */
function applyExecuteSkill(state: GameState, { monsterIndex, skillType }: ExecuteSkillPayload): GameState {
  const player = getCurrentPlayer(state);
  const monster = player.field[monsterIndex];

  if (!monster) {
    return addLog(state, 'モンスターが見つかりません', 'info');
  }

  // 状態異常チェック（CAGED, STUN, SILENCE, THUNDER等）
  const { statusEffectEngine } = require('../statusEffects');
  if (!statusEffectEngine.canUseSkill(monster)) {
    const reason = statusEffectEngine.getBlockingStatusName(monster, 'skill');
    return addLog(state, `${monster.name}は${reason}のため技を使用できません`, 'info');
  }

  if (monster.usedSkillThisTurn) {
    return addLog(state, 'このモンスターは既に技を使用しています', 'info');
  }

  // チャージ確認
  const requiredCharges = skillType === 'basic' ? 1 : 2;
  const chargesCount = monster.charges?.length || 0;
  if (chargesCount < requiredCharges) {
    return addLog(state, `チャージが足りません（必要: ${requiredCharges}, 現在: ${chargesCount}）`, 'info');
  }

  // スキルテキストを取得
  // basicSkill/advancedSkillはオブジェクト（{text, attribute, cost}）または文字列
  const skillData = skillType === 'basic' ? monster.basicSkill : monster.advancedSkill;
  const skillText = typeof skillData === 'object' ? skillData?.text : skillData;

  if (!skillText) {
    return addLog(state, '技が見つかりません', 'info');
  }

  // 技使用済みフラグを設定し、チャージを消費
  const newCharges = [...(Array.isArray(monster.charges) ? monster.charges : [])];
  for (let i = 0; i < requiredCharges; i++) {
    newCharges.shift();
  }

  const newField = [...player.field];
  newField[monsterIndex] = {
    ...monster,
    usedSkillThisTurn: true,
    charges: newCharges,
  };

  let newState = updatePlayer(state, state.currentPlayer, { field: newField });
  newState = addLog(newState, `${monster.name}が${skillType === 'basic' ? '基本技' : '上級技'}を発動！`, 'info');

  // contextアダプターを使用して技効果を実行
  try {
    const { createPureContext } = require('./contextAdapter');
    const { executeSkillEffects } = require('../effectEngine');

    const context = createPureContext(newState, {
      monsterIndex,
      skillType,
      card: monster,
    });

    // 技効果を実行
    executeSkillEffects(skillText, context, monster.id);

    // 更新されたstateを取得
    newState = context.getState();
  } catch (error) {
    // effectEngineが使えない場合はログのみ
    console.warn('Effect engine not available:', error);
    newState = addLog(newState, '(シミュレーション: 技効果省略)', 'info');
  }

  return newState;
}

/**
 * カードをチャージ
 */
function applyChargeCard(state: GameState, { handCardIndex, monsterIndex }: ChargeCardPayload): GameState {
  if (state.turnFlags.chargeUsedThisTurn) {
    return addLog(state, 'このターンは既にチャージしています', 'info');
  }

  const player = getCurrentPlayer(state);
  const card = player.hand[handCardIndex];
  const monster = player.field[monsterIndex];

  if (!card || !monster) {
    return addLog(state, 'カードまたはモンスターが見つかりません', 'info');
  }

  // 同属性または「なし」属性のみチャージ可能
  if (card.attribute !== monster.attribute && card.attribute !== 'なし') {
    return addLog(state, '属性が一致しないためチャージできません', 'info');
  }

  // 手札からカードを削除
  const newHand = player.hand.filter((_, i) => i !== handCardIndex);

  // モンスターにチャージを追加
  const charges = Array.isArray(monster.charges) ? monster.charges : [];
  const newCharges = [...charges, { card, type: 'card' }];

  const newField = [...player.field];
  newField[monsterIndex] = {
    ...monster,
    charges: newCharges,
  };

  let newState = updatePlayer(state, state.currentPlayer, {
    hand: newHand,
    field: newField,
  });

  newState = updateTurnFlags(newState, { chargeUsedThisTurn: true });
  newState = addLog(newState, `${card.name}を${monster.name}にチャージ`, 'info');

  return newState;
}

/**
 * SPをチャージ
 */
function applyChargeSP(state: GameState, { monsterIndex }: ChargeSPPayload): GameState {
  if (state.turnFlags.chargeUsedThisTurn) {
    return addLog(state, 'このターンは既にチャージしています', 'info');
  }

  const player = getCurrentPlayer(state);
  const monster = player.field[monsterIndex];

  if (!monster) {
    return addLog(state, 'モンスターが見つかりません', 'info');
  }

  if (player.activeSP < 1) {
    return addLog(state, 'SPが足りません', 'info');
  }

  // SPを消費
  const newActiveSP = player.activeSP - 1;

  // モンスターにSPチャージを追加
  const charges = Array.isArray(monster.charges) ? monster.charges : [];
  const newCharges = [...charges, { type: 'sp' }];

  const newField = [...player.field];
  newField[monsterIndex] = {
    ...monster,
    charges: newCharges,
  };

  let newState = updatePlayer(state, state.currentPlayer, {
    activeSP: newActiveSP,
    field: newField,
  });

  newState = updateTurnFlags(newState, { chargeUsedThisTurn: true });
  newState = addLog(newState, `SPを${monster.name}にチャージ`, 'info');

  return newState;
}

/**
 * 魔法カード使用（effectEngine統合版）
 * contextアダプターを使用して完全な魔法効果を実行
 */
function applyUseMagic(state: GameState, { cardIndex }: UseMagicPayload): GameState {
  const player = getCurrentPlayer(state);
  const card = player.hand[cardIndex];

  if (!card || !canUseMagic(state, card)) {
    return addLog(state, '魔法カードを使用できません', 'info');
  }

  // 手札からカードを削除
  const newHand = player.hand.filter((_, i) => i !== cardIndex);

  // SPを消費（activeSP優先、足りなければbonusSP使用）
  const magicBonusSP = player.bonusSP || 0;
  const magicFromActive = Math.min(player.activeSP, card.cost);
  const magicFromBonus = card.cost - magicFromActive;
  const newActiveSP = player.activeSP - magicFromActive;
  const newRestedSP = player.restedSP + magicFromActive;
  const newBonusSP = magicBonusSP - magicFromBonus;

  // 墓地に送る
  const newGraveyard = [...player.graveyard, card];

  let newState = updatePlayer(state, state.currentPlayer, {
    hand: newHand,
    activeSP: newActiveSP,
    restedSP: newRestedSP,
    bonusSP: newBonusSP,
    graveyard: newGraveyard,
  });

  if (magicFromBonus > 0) {
    newState = addLog(newState, `☆ボーナスSPを${magicFromBonus}使用！`, 'heal');
  }
  newState = addLog(newState, `${card.name}を発動！`, 'info');

  // カードプレイ履歴を記録
  newState = recordCardPlay(newState, state.currentPlayer, card, 'magic');

  // contextアダプターを使用して魔法効果を実行
  try {
    const { createPureContext } = require('./contextAdapter');
    const { executeSkillEffects } = require('../effectEngine');

    const context = createPureContext(newState, {
      card: card,
      turn: newState.turn,
    });

    // 魔法効果を実行（effectはカードのeffectフィールド）
    const effectText = card.effect || '';
    if (effectText) {
      executeSkillEffects(effectText, context, card.id);
    }

    // 更新されたstateを取得
    newState = context.getState();
  } catch (error) {
    // effectEngineが使えない場合はログのみ
    console.warn('Magic effect engine not available:', error);
    newState = addLog(newState, '(シミュレーション: 魔法効果省略)', 'info');
  }

  // 自分が魔法を発動した時のトリガーを発火（呪核の杖・ザルヴェリオ等のフェイズカード効果）
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SELF_MAGIC_ACTIVATED, { card });

  return newState;
}

/**
 * カードをドロー
 */
function applyDrawCard(state: GameState, { player, count = 1 }: DrawCardPayload): GameState {
  const playerData = getPlayer(state, player);

  if (playerData.deck.length === 0) {
    return addLog(state, 'デッキが空です', 'info');
  }

  const drawCount = Math.min(count, playerData.deck.length);
  const drawnCards = playerData.deck.slice(0, drawCount);
  const remainingDeck = playerData.deck.slice(drawCount);
  const newHand = [...playerData.hand, ...drawnCards];

  let newState = updatePlayer(state, player, {
    deck: remainingDeck,
    hand: newHand,
  });

  const cardNames = drawnCards.map(c => c.name).join(', ');
  newState = addLog(newState, `P${player}が${drawCount}枚ドロー: ${cardNames}`, 'info');

  return newState;
}

/**
 * ダメージを与える
 */
function applyDealDamage(state: GameState, { target, amount }: DealDamagePayload): GameState {
  // target: 'p1' | 'p2' | 1 | 2
  const player = typeof target === 'string' ? (target === 'p1' ? 1 : 2) : target;
  const playerData = getPlayer(state, player);

  const newLife = Math.max(0, playerData.life - amount);

  let newState = updatePlayer(state, player, { life: newLife });
  newState = addLog(newState, `P${player}に${amount}ダメージ！`, 'damage');

  return applyCheckWinner(newState);
}

/**
 * ライフを回復
 */
function applyHealLife(state: GameState, { target, amount }: HealLifePayload): GameState {
  const player = typeof target === 'string' ? (target === 'p1' ? 1 : 2) : target;
  const playerData = getPlayer(state, player);

  const newLife = playerData.life + amount;

  let newState = updatePlayer(state, player, { life: newLife });
  newState = addLog(newState, `P${player}が${amount}回復！`, 'heal');

  return newState;
}

/**
 * モンスターを破壊
 */
function applyDestroyMonster(state: GameState, { player, slotIndex }: DestroyMonsterPayload): GameState {
  const playerData = getPlayer(state, player);
  const monster = playerData.field[slotIndex];

  if (!monster) {
    return state;
  }

  const newField = [...playerData.field];
  newField[slotIndex] = null;

  const newGraveyard = [...playerData.graveyard, monster];

  let newState = updatePlayer(state, player, {
    field: newField,
    graveyard: newGraveyard,
  });

  newState = addLog(newState, `${monster.name}が破壊された！`, 'damage');

  // TODO: ON_DESTROY_SELFトリガー

  return newState;
}

/**
 * 勝敗判定
 */
function applyCheckWinner(state: GameState): GameState {
  const winner = checkWinner(state);

  if (winner !== null) {
    return applySetWinner(state, { winner });
  }

  return state;
}

/**
 * 勝者を設定
 */
function applySetWinner(state: GameState, { winner }: SetWinnerPayload): GameState {
  let newState = updateGameProgress(state, {
    winner,
    gameStatus: GAME_STATUS.GAME_OVER,
  });

  newState = addLog(newState, `🏆 P${winner}の勝利！`, 'info');

  return newState;
}

// ========================================
// Phase B-5: フィールドカード・フェイズカード
// ========================================

/**
 * フィールドカードを配置
 */
function applyPlaceFieldCard(state: GameState, { cardIndex }: PlaceFieldCardPayload): GameState {
  const player = getCurrentPlayer(state);
  const card = player.hand[cardIndex];

  if (!card || card.type !== 'field') {
    return addLog(state, 'フィールドカードが見つかりません', 'info');
  }

  // SPチェック（activeSP + bonusSP）
  const fieldBonusSP = player.bonusSP || 0;
  const fieldTotalAvailableSP = player.activeSP + fieldBonusSP;
  if (card.cost > fieldTotalAvailableSP) {
    return addLog(state, 'SPが足りません', 'info');
  }

  // 手札からカードを削除
  const newHand = player.hand.filter((_, i) => i !== cardIndex);

  // フィールドカードを設定（owner追加）
  const fieldCardInstance = { ...card, owner: state.currentPlayer };

  // SP消費（activeSP優先、足りなければbonusSP使用）
  const fieldFromActive = Math.min(player.activeSP, card.cost);
  const fieldFromBonus = card.cost - fieldFromActive;
  const newActiveSP = player.activeSP - fieldFromActive;
  const newRestedSP = player.restedSP + fieldFromActive;
  const newBonusSP = fieldBonusSP - fieldFromBonus;

  let newState = updatePlayer(state, state.currentPlayer, {
    hand: newHand,
    fieldCard: fieldCardInstance,
    activeSP: newActiveSP,
    restedSP: newRestedSP,
    bonusSP: newBonusSP,
  });

  if (fieldFromBonus > 0) {
    newState = addLog(newState, `☆ボーナスSPを${fieldFromBonus}使用！`, 'heal');
  }
  newState = addLog(newState, `P${state.currentPlayer}: ${card.name}を設置！`, 'info');

  // カードプレイ履歴を記録
  newState = recordCardPlay(newState, state.currentPlayer, card, 'field');

  // フィールドカードのトリガーを登録（slotIndex は null）
  newState = registerCardTriggersPure(newState, fieldCardInstance, state.currentPlayer, null);

  return newState;
}

/**
 * フェイズカードを配置
 */
function applyPlacePhaseCard(state: GameState, { cardIndex }: PlacePhaseCardPayload): GameState {
  const player = getCurrentPlayer(state);
  const card = player.hand[cardIndex];

  if (!card || card.type !== 'phasecard') {
    return addLog(state, 'フェイズカードが見つかりません', 'info');
  }

  // SPチェック（activeSP + bonusSP）
  const phaseBonusSP = player.bonusSP || 0;
  const phaseTotalAvailableSP = player.activeSP + phaseBonusSP;
  if (card.cost > phaseTotalAvailableSP) {
    return addLog(state, 'SPが足りません', 'info');
  }

  // 手札からカードを削除
  const newHand = player.hand.filter((_, i) => i !== cardIndex);

  // フェイズカードを設定（stage, stackCount, charges, owner追加）
  const phaseCardInstance: PhaseCardInstance = {
    ...card,
    uniqueId: card.uniqueId,
    stage: 0,
    stackCount: 0,
    charges: [],
    owner: state.currentPlayer,
  };

  // SP消費（activeSP優先、足りなければbonusSP使用）
  const phaseFromActive = Math.min(player.activeSP, card.cost);
  const phaseFromBonus = card.cost - phaseFromActive;
  const newActiveSP = player.activeSP - phaseFromActive;
  const newRestedSP = player.restedSP + phaseFromActive;
  const newBonusSP = phaseBonusSP - phaseFromBonus;

  let newState = updatePlayer(state, state.currentPlayer, {
    hand: newHand,
    phaseCard: phaseCardInstance,
    activeSP: newActiveSP,
    restedSP: newRestedSP,
    bonusSP: newBonusSP,
  });

  if (phaseFromBonus > 0) {
    newState = addLog(newState, `☆ボーナスSPを${phaseFromBonus}使用！`, 'heal');
  }
  newState = addLog(newState, `P${state.currentPlayer}: フェイズカード【${card.name}】を設置！【初期段階】`, 'info');

  // カードプレイ履歴を記録
  newState = recordCardPlay(newState, state.currentPlayer, card, 'phasecard');

  // フェイズカードのトリガーを登録（slotIndex は null）
  newState = registerCardTriggersPure(newState, phaseCardInstance, state.currentPlayer, null);

  return newState;
}

/**
 * フェイズカードへのチャージ
 * 手札のカードをフェイズカードに重ねて段階を進める
 */
function applyChargeToPhaseCard(state: GameState, { cardIndex }: ChargeToPhaseCardPayload): GameState {
  const player = getCurrentPlayer(state);
  const card = player.hand[cardIndex];
  const phaseCard = player.phaseCard;

  // バリデーション
  if (!card) {
    return addLog(state, 'カードが見つかりません', 'info');
  }

  if (!phaseCard) {
    return addLog(state, 'フェイズカードが設置されていません', 'info');
  }

  // 段階チェック
  const currentStage = phaseCard.stage !== undefined ? phaseCard.stage : (phaseCard.charges?.length || 0);
  if (currentStage >= 3) {
    return addLog(state, 'フェイズカードは既に最終段階です', 'info');
  }

  // チャージ済みチェック（モンスターチャージと共有）
  if (state.turnFlags.chargeUsedThisTurn) {
    return addLog(state, 'このターンは既にチャージしています', 'info');
  }

  // 属性チェック
  const isNoneAttribute = phaseCard.attribute === 'なし';
  if (!isNoneAttribute && card.attribute !== phaseCard.attribute && card.attribute !== 'なし') {
    return addLog(state, '属性が一致しないためチャージできません', 'info');
  }

  // カード種類チェック
  if (!['monster', 'magic', 'field', 'phase', 'phasecard'].includes(card.type)) {
    return addLog(state, 'このカードはチャージできません', 'info');
  }

  // 手札からカードを削除
  const newHand = player.hand.filter((_, i) => i !== cardIndex);

  // フェイズカードの段階を進める
  const newCharges = [...(Array.isArray(phaseCard.charges) ? phaseCard.charges : []), card];
  const newStage = currentStage + 1;
  const newPhaseCard = {
    ...phaseCard,
    stage: newStage,
    charges: newCharges,
  };

  let newState = updatePlayer(state, state.currentPlayer, {
    hand: newHand,
    phaseCard: newPhaseCard,
  });

  // チャージフラグを設定
  newState = updateTurnFlags(newState, { chargeUsedThisTurn: true });

  const stageNames = ['初期段階', '1枚重ね', '2枚重ね', '3枚重ね（最終）'];
  newState = addLog(newState, `${card.name}を【${phaseCard.name}】にチャージ！→【${stageNames[newStage]}】`, 'info');

  // 最終段階(3枚重ね)になった場合はフェイズカード発動→墓地送り処理はUI側で行う
  // （効果発動タイミングの制御が必要なため、ここでは段階更新のみ）

  return newState;
}

// ========================================
// Phase D-4: 効果コンテキスト用アクション
// ========================================

/**
 * プレイヤー状態の部分更新
 * effectHelpers/cardEffects/cardTriggersからのset*呼び出しをdispatch経由に変換
 */
function applyUpdatePlayerState(state: GameState, { player, updates }: UpdatePlayerStatePayload): GameState {
  // 関数形式の値をサポート（setState(prev => prev + 1)形式）
  const resolvedUpdates: Record<string, unknown> = {};
  const playerData = getPlayer(state, player);
  const playerDataIndexed = playerData as unknown as Record<string, unknown>;

  for (const [key, value] of Object.entries(updates)) {
    if (typeof value === 'function') {
      // 関数形式: 現在の値を引数に取り、新しい値を返す
      resolvedUpdates[key] = (value as (prev: unknown) => unknown)(playerDataIndexed[key]);
    } else {
      // 直接値形式
      resolvedUpdates[key] = value;
    }
  }

  let newState = updatePlayer(state, player, resolvedUpdates as Partial<PlayerState>);

  // ライフが更新された場合、勝敗判定を行う
  if ('life' in resolvedUpdates) {
    newState = applyCheckWinner(newState);
  }

  return newState;
}

/**
 * ゲームフラグの更新
 * chargeUsedThisTurn等のターンフラグを更新
 */
function applySetGameFlags(state: GameState, { flags }: SetGameFlagsPayload): GameState {
  return updateTurnFlags(state, flags);
}

// ========================================
// 深海領域アクション実装
// ========================================

/**
 * 深海領域を開放
 */
function applyUnlockDeepSeaZone(state: GameState, { player }: UnlockDeepSeaZonePayload): GameState {
  const playerData = getPlayer(state, player);

  // 既に開放されている場合は何もしない
  if (playerData.deepSeaZone.isUnlocked) {
    return state;
  }

  const updatedZone = unlockDeepSeaZone(playerData.deepSeaZone);
  let newState = updatePlayer(state, player, { deepSeaZone: updatedZone });
  newState = addLog(newState, `P${player}の深海領域が開放された！`, 'info');

  return newState;
}

/**
 * カードを深海領域に潜航させる
 */
function applySubmergeCard(state: GameState, { player, cardIndex, initialDepth }: SubmergeCardPayload): GameState {
  const playerData = getPlayer(state, player);
  const card = playerData.hand[cardIndex];

  if (!card) {
    return addLog(state, '潜航失敗: カードが見つかりません', 'info');
  }

  // 潜航処理
  const result = submergeCard(
    playerData.deepSeaZone,
    card,
    state.turn,
    initialDepth
  );

  if (!result.success) {
    return addLog(state, result.message, 'info');
  }

  // 手札からカードを削除
  const updatedHand = playerData.hand.filter((_, idx) => idx !== cardIndex);

  let newState = updatePlayer(state, player, {
    deepSeaZone: result.updatedZone,
    hand: updatedHand,
  });

  newState = addLog(newState, result.message, 'info');

  // ON_SUBMERGE トリガーを発火
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SUBMERGE, {
    card,
    player,
    depth: initialDepth || 0,
  });

  return newState;
}

/**
 * カードを深海領域から浮上させる
 */
function applySurfaceCard(
  state: GameState,
  { player, deepSeaIndex, fieldSlot, costReduction = 0 }: SurfaceCardPayload
): GameState {
  const playerData = getPlayer(state, player);

  // 浮上処理
  const result = surfaceCard(playerData.deepSeaZone, deepSeaIndex, costReduction);

  if (!result.success || !result.surfacedCard) {
    return addLog(state, result.message, 'info');
  }

  // SPチェック
  if (playerData.activeSP < result.spCost) {
    return addLog(state, `浮上失敗: SPが足りません（必要: ${result.spCost}、現在: ${playerData.activeSP}）`, 'info');
  }

  // フィールドスロットチェック
  if (playerData.field[fieldSlot] !== null) {
    return addLog(state, '浮上失敗: 指定スロットにモンスターがいます', 'info');
  }

  // モンスターインスタンスを作成
  const monsterInstance = createMonsterInstance(result.surfacedCard);

  // 深度ボーナスを適用
  const bonus = result.bonus;
  monsterInstance.currentAttack = (monsterInstance.currentAttack || monsterInstance.attack || 0) + bonus.attackBonus;
  monsterInstance.attack = (monsterInstance.attack || 0) + bonus.attackBonus;

  // 先制攻撃可能な場合は召喚酔いなし
  monsterInstance.canAttack = bonus.canPreemptiveStrike;

  // オーナー設定
  monsterInstance.owner = player;

  // フィールドに配置
  const updatedField = [...playerData.field];
  updatedField[fieldSlot] = monsterInstance;

  // SP消費
  const updatedActiveSP = playerData.activeSP - result.spCost;
  const updatedRestedSP = playerData.restedSP + result.spCost;

  let newState = updatePlayer(state, player, {
    deepSeaZone: result.updatedZone,
    field: updatedField,
    activeSP: updatedActiveSP,
    restedSP: updatedRestedSP,
  });

  newState = addLog(newState, result.message, 'info');

  // トリガー登録（召喚時トリガーと同様）
  newState = registerCardTriggersPure(newState, monsterInstance, player, fieldSlot);

  // ON_SURFACE トリガーを発火
  // 深度5+の場合は追加の浮上時効果も発動
  newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SURFACE, {
    card: monsterInstance,
    player,
    slotIndex: fieldSlot,
    depth: result.surfaceDepth,
    attackBonus: bonus.attackBonus,
    canPreemptiveStrike: bonus.canPreemptiveStrike,
    triggerSurfaceEffect: bonus.triggerSurfaceEffect,
  });

  return newState;
}

/**
 * 深海領域の深度を増加
 */
function applyIncrementDepth(state: GameState, { player, bonusDepth = 0 }: IncrementDepthPayload): GameState {
  const playerData = getPlayer(state, player);

  if (playerData.deepSeaZone.cards.length === 0) {
    return state;
  }

  const result = incrementDepth(playerData.deepSeaZone, bonusDepth);

  let newState = updatePlayer(state, player, { deepSeaZone: result.updatedZone });

  const bonusText = result.bonusDepth > 0 ? `（+${result.bonusDepth}ボーナス）` : '';
  newState = addLog(
    newState,
    `P${player}の深海領域: ${result.incrementedCount}枚の深度が上昇${bonusText}`,
    'info'
  );

  return newState;
}

/**
 * 深海領域の上限を設定
 */
function applySetDeepSeaCapacity(state: GameState, { player, capacity }: SetDeepSeaCapacityPayload): GameState {
  const playerData = getPlayer(state, player);
  const updatedZone = setDeepSeaCapacity(playerData.deepSeaZone, capacity);

  let newState = updatePlayer(state, player, { deepSeaZone: updatedZone });
  newState = addLog(newState, `P${player}の深海領域上限が${capacity}枚に変更`, 'info');

  return newState;
}

/**
 * 深海領域のカードを破壊
 */
function applyDestroyDeepSeaCard(state: GameState, { player, deepSeaIndex }: DestroyDeepSeaCardPayload): GameState {
  const playerData = getPlayer(state, player);
  const { updatedZone, destroyedCard } = destroyDeepSeaCard(playerData.deepSeaZone, deepSeaIndex);

  if (!destroyedCard) {
    return addLog(state, '破壊失敗: カードが見つかりません', 'info');
  }

  // 墓地に送る
  const updatedGraveyard = [...playerData.graveyard, destroyedCard];

  let newState = updatePlayer(state, player, {
    deepSeaZone: updatedZone,
    graveyard: updatedGraveyard,
  });

  newState = addLog(newState, `${destroyedCard.name}が深海領域から破壊された`, 'damage');

  return newState;
}
